'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { API_ROUTES, HTTP_METHODS, useApiMutation, useApiQuery } from '@shared/api-utils';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { ChevronLeft } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepFormProvider } from '../../../form-controls/MultistepForm/context/MultistepFormContext';
import { useMultistepFormState } from '../../../form-controls/MultistepForm/hooks/useMultistepFormState';
import { useGlobalDialogPolling } from '../../hooks/useGlobalDialogPolling';
import { BackHandler } from './components/BackHandler';
import { CompleteStep } from './steps/CompleteStep';
import { ErrorStep } from './steps/ErrorStep';
import { IndustryStep } from './steps/IndustryStep';
import { OccupationStep } from './steps/OccupationStep';
import { PayStep } from './steps/PayStep';
import { SpendStep } from './steps/SpendStep';
import { StatementStep } from './steps/StatementStep';
import { WelcomeStep } from './steps/WelcomeStep';
import type { AffordabilityMicQuestionnaireResponse, AffordabilityQuestionApi, QuestionnaireResponse } from './types';
import { transformQuestionnaireData } from './utils/transformQuestionnaireData';

/**
 * Displays and controls the Affordability Questionnaire dialog.
 *
 * Behaviour overview:
 * - Listens to shared long polling for global popup messages via useGlobalDialogPolling hook.
 * - Opens the dialog only when the server signals an `AFFORDABILITY_MIC_QUESTIONNAIRE` message.
 * - Registers its open state with the polling provider to pause polling when open (prevents race conditions).
 * - Uses useMultistepFormState to guide the user through 7 steps of affordability assessment.
 * - Caches the popup `id` in a ref the moment the dialog opens.
 * - Submits collected data to the affordability questionnaire endpoint on completion.
 * - Confirmation step shows result-specific copy: POSITIVE (spend limit and guidance) or CONCERN (temporary suspension notice).
 *
 * Testing in development:
 * - Long polling is always enabled in client-side environments.
 * - When API_MOCKING=true (local dev/test), use `?popup=AFFORDABILITY_MIC_QUESTIONNAIRE` to trigger the dialog.
 * - In production, popups are triggered by actual server responses, not URL parameters.
 */
function AffordabilityQuestionnaireDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [questionnaireResult, setQuestionnaireResult] = useState<AffordabilityMicQuestionnaireResponse | null>(null);
  const skipToStepRef = useRef<((stepId: string) => void) | null>(null);
  const t = useTranslations('global_dialogs.affordability_questionnaire');
  const tCommon = useTranslations('common');
  const latestDialogIdRef = useRef<string>('');

  const debug = createDebug({ isEnabled: true });

  const POPUP_TYPE = 'AFFORDABILITY_MIC_QUESTIONNAIRE';

  const {
    data: questionsApiResponse,
    isLoading: isQuestionsLoading,
    isError: isQuestionsError,
  } = useApiQuery<AffordabilityQuestionApi[]>(API_ROUTES.PLAYER_KYC.AFFORDABILITY.QUESTIONS);

  // Use shared polling hook - registers this dialog's open state and receives polling data
  const { data } = useGlobalDialogPolling(POPUP_TYPE, isOpen);

  // Define form schema
  const formSchema = z.object({
    welcome: z.any().optional(),
    statement: z.string().min(1, t('errors.statement_required')),
    occupation: z.string().min(1, t('errors.occupation_required')),
    industry: z.string().min(1, t('errors.industry_required')),
    pay: z.string().min(1, t('errors.pay_required')),
    spend: z.string().min(1, t('errors.spend_required')),
    confirmation: z.any().optional(),
  });

  type FormData = z.infer<typeof formSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    defaultValues: {
      statement: '',
      occupation: '',
      industry: '',
      pay: '',
      spend: '',
    },
  });

  const { mutateAsync: submitAffordability, isPending: isSubmittingAffordability } = useApiMutation<AffordabilityMicQuestionnaireResponse, QuestionnaireResponse>(
    API_ROUTES.PLAYER_KYC.AFFORDABILITY.QUESTIONNAIRE,
    HTTP_METHODS.POST
  );

  useEffect(() => {
    if (data?.messageType === POPUP_TYPE && !isOpen && data.id?.trim()) {
      latestDialogIdRef.current = data.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(true);
    }
  }, [data?.messageType, data?.id, isOpen]);

  const handleComplete = async () => {
    const formValues = form.getValues();
    const questions = questionsApiResponse ?? [];
    const questionsDataTranslated: Record<string, string> = {};

    questions.forEach(question => {
      const questionKey = `question${question.order}`;
      questionsDataTranslated[questionKey] = t(question.label);
      question.options.forEach(optionKey => {
        questionsDataTranslated[optionKey] = t(optionKey);
      });
    });

    const submissionData = transformQuestionnaireData(formValues as Record<string, string>, questionsDataTranslated, latestDialogIdRef.current);
    const response = await submitAffordability(submissionData);

    setQuestionnaireResult(response);
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowBackButton(false);
    setShowCloseButton(false);
    setQuestionnaireResult(null);
    form.reset();
    skipToStepRef.current?.('welcome');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- goBack provided by BackHandler but we use formState.goToPreviousStep
  const handleStepInfo = useCallback((stepIndex: number, _goBack: () => void) => {
    const showBack = stepIndex >= 2 && stepIndex <= 5;
    const showClose = stepIndex === 6;
    setShowBackButton(showBack);
    setShowCloseButton(showClose);
  }, []);

  const handleMultistepFormReady = useCallback((skipToStep: (stepId: string) => void) => {
    skipToStepRef.current = skipToStep;
  }, []);

  const questions = questionsApiResponse ?? [];
  const getQuestion = (order: number) => questions.find(question => question.order === order)!;

  const completeStepDataTrackContainer =
    questionnaireResult?.result === 'POSITIVE' ? 'Affordability Pass' : questionnaireResult?.result === 'CONCERN' ? 'Affordability Fail' : 'Affordability Complete';

  const steps = [
    {
      id: 'welcome',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <WelcomeStep />
        </>
      ),
      dataTrackContainer: 'Affordability Welcome',
      schema: z.any(),
    },
    {
      id: 'statement',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <StatementStep question={getQuestion(1)} />
        </>
      ),
      dataTrackContainer: 'Affordability Statement',
      schema: formSchema.pick({ statement: true }),
    },
    {
      id: 'occupation',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <OccupationStep question={getQuestion(2)} />
        </>
      ),
      dataTrackContainer: 'Affordability Occupation',
      schema: formSchema.pick({ occupation: true }),
    },
    {
      id: 'industry',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <IndustryStep question={getQuestion(3)} />
        </>
      ),
      dataTrackContainer: 'Affordability Industry',
      schema: formSchema.pick({ industry: true }),
    },
    {
      id: 'pay',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <PayStep question={getQuestion(4)} />
        </>
      ),
      dataTrackContainer: 'Affordability Pay',
      schema: formSchema.pick({ pay: true }),
    },
    {
      id: 'spend',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <SpendStep question={getQuestion(5)} onComplete={handleComplete} isSubmitting={isSubmittingAffordability} />
        </>
      ),
      dataTrackContainer: 'Affordability Spend',
      schema: formSchema.pick({ spend: true }),
    },
    {
      id: 'confirmation',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          {questionnaireResult ? (
            <CompleteStep
              result={questionnaireResult.result}
              value={questionnaireResult.value}
              onClose={handleClose}
              questions={questions}
              dataTrackContainer={completeStepDataTrackContainer}
            />
          ) : (
            <ErrorStep onRetry={() => skipToStepRef.current?.('spend')} />
          )}
        </>
      ),
      dataTrackContainer: completeStepDataTrackContainer,
      schema: z.any(),
    },
  ];

  const formState = useMultistepFormState({
    steps,
    onComplete: () => {},
    onMultistepFormReady: handleMultistepFormReady,
    formMethods: form as unknown as Parameters<typeof useMultistepFormState>[0]['formMethods'],
  });

  const handleBackClick = () => {
    formState.goToPreviousStep();
  };

  const handleOpenChange = (openState: boolean) => {
    if (!openState) {
      setIsOpen(false);
      setShowBackButton(false);
      setShowCloseButton(false);
      setQuestionnaireResult(null);
      form.reset();
      skipToStepRef.current?.('welcome');
    }
  };

  if (isQuestionsError) {
    debug.forceError('[Affordability Questionnaire Dialog] Failed to load questions data - dialog will not be shown');
    return null;
  }

  if (isQuestionsLoading || !questionsApiResponse) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="affordability-questionnaire-dialog"
        showCloseButton={showCloseButton}
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        data-track-feature="My Account"
      >
        <div
          key={`affordability-step-${formState.currentStepIndex}`}
          data-testid="affordability-current-step"
          data-step-index={formState.currentStepIndex}
          className="flex min-h-0 flex-1 flex-col md:min-h-fit"
          data-track-container={formState.currentStep?.dataTrackContainer}
        >
          <DialogHeader className="relative flex items-center justify-center px-5 border-b-1 border-border">
            {showBackButton && (
              <Button
                variant="ghost"
                onClick={handleBackClick}
                className="absolute -mt-2 left-5 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full p-0"
                type="button"
                aria-label={tCommon('back')}
                data-track-name="Back"
                data-testid="dialog-back-button"
              >
                <ChevronLeft size="small" />
              </Button>
            )}
            <DialogTitle className="paragraph-explanatory-strong">{t('title')}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">{t('welcome.message_intro')}</DialogDescription>

          <FormProvider {...form}>
            <MultistepFormProvider value={formState}>
              <div className="flex min-h-0 flex-1 flex-col">{formState.currentStep?.component}</div>
            </MultistepFormProvider>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { AffordabilityQuestionnaireDialog };
